import { FC, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CopyModelDataType, DataOfCopiedModelType, DialogCopyModelType } from 'src/types'
import { FormGenerator, ModalWrapper } from '@microservices/wiskey-react-components'
import { Box } from '@mui/material'

import { useEntityTitleDuplicateValidation } from '@hooks'
import { GENERATOR_INPUT_TYPE, REGEX } from '@constants'

type CopyDialogProps = {
  isShow: true
  onClose: () => void
  copyData: DataOfCopiedModelType
  onCopy: (data: CopyModelDataType) => void
  models: string[]
  dialogTitle: string
  loading?: boolean
  hideObject?: boolean
  titleEntityType?: 'view' | 'form'
}

export const CopyDialog: FC<CopyDialogProps> = ({
  isShow,
  onClose,
  copyData,
  onCopy,
  models,
  dialogTitle,
  loading,
  hideObject,
  titleEntityType = 'view',
}) => {
  const methods = useForm<DialogCopyModelType>()
  const { t } = useTranslation()

  const titleValidation = useEntityTitleDuplicateValidation({ entityType: titleEntityType })

  const {
    handleSubmit,
    reset,
    formState: { isDirty },
  } = methods

  useEffect(() => {
    reset({ title: copyData.title, objectCode: copyData.objectCode })
  }, [copyData])

  const handleSave = (formValue: DialogCopyModelType) => {
    const { code, title } = formValue

    onCopy({ code, title, copyId: copyData.id })
  }

  const handleClose = () => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    onClose()
  }

  const validateInputTitle = (value: string) => {
    if (value.trim().length === 0) return t('error.onlyWhitespacesTitleInput')
    if (!REGEX.CHECK_LETTERS_DASH_UNDERLINE_WHITESPACE_NUMBERS.test(value))
      return t('error.notValidSymbolsTitleInput')

    return titleValidation.validate(value, 'copy')
  }

  return (
    <ModalWrapper
      disabledSave={loading}
      isShow={isShow}
      title={dialogTitle}
      containerStyle={{
        px: 0.5,
        py: 0.5,
        // borderRadius: 0
      }}
      onClose={handleClose}
      onSave={handleSubmit(handleSave)}
    >
      <Box my={1}>
        <FormProvider {...methods}>
          <FormGenerator
            mainGridSpacing={0}
            inputs={[
              {
                name: 'code',
                inputType: GENERATOR_INPUT_TYPE.INPUT,
                label: t('copying.inputs.code.label'),
                placeholder: t('copying.inputs.code.placeholder'),
                replacePattern: REGEX.MODEL_CODE_REPLACE_PATTERN,
                rules: {
                  required: true,
                  validate: value => {
                    if (models.findIndex(model => model === value) !== -1) {
                      return `${value} ${t('columnInputs.errors.alreadyUsed')}`
                    }
                  },
                },
              },
              {
                name: 'title',
                inputType: GENERATOR_INPUT_TYPE.INPUT,
                label: t('copying.inputs.title.label'),
                placeholder: t('copying.inputs.title.placeholder'),
                rules: { required: true, validate: validateInputTitle },
              },
              ...(!hideObject
                ? [
                    {
                      name: 'objectCode',
                      inputType: GENERATOR_INPUT_TYPE.SELECT,
                      selectOptions: [
                        { id: 0, value: copyData.objectCode, name: copyData.objectTitle },
                      ],
                      disabled: true,
                      label: t('copying.inputs.objectCode.label'),
                    },
                  ]
                : []),
            ]}
          />
        </FormProvider>
      </Box>
    </ModalWrapper>
  )
}
